/**
 * bs-ga4.js — GA4 sink for the ballstories storefront.
 *
 * Mirrors the configurator funnel that `bs-clarity.js` already measures into
 * Google Analytics 4, so the steps can be analysed the way the old Vue app's
 * funnel report worked. Clarity events carry no parameters (the dimension has
 * to live in the event name), which is exactly what makes them awkward to
 * report on; GA4 does have parameters, so here one event name carries `step`,
 * `field`, `book_type` and friends as dimensions.
 *
 * This file owns two things and nothing else:
 *   1. getting gtag.js onto the page, consent-gated and without blocking LCP
 *   2. translating the canonical `bs:event` / `bs:tag` stream into GA4 events
 *
 * It never observes the DOM for friction itself — that logic lives in exactly
 * one place (`bs-clarity.js`), which re-emits every canonical event.
 *
 * Scope is deliberately narrow: **only** events that happen inside the
 * configurator. Everything standard — page_view, view_item, add_to_cart,
 * begin_checkout, purchase — is already sent to the same GA4 property by the
 * Google & YouTube channel's web pixel, and sending them again here would
 * double every number in the funnel. Hence `send_page_view: false`.
 *
 * Consent: nothing is sent before Shopify's customer privacy API reports that
 * analytics processing is allowed. Until then events queue in `dataLayer` and
 * are flushed once (and only if) gtag.js loads. Google Consent Mode v2 defaults
 * are set to `denied` up front, so even a third-party gtag on the page cannot
 * write cookies on our behalf before the visitor decided.
 *
 * Configuration comes from `snippets/bs-ga4.liquid` (`window.bsGA4Config`).
 * An empty measurement id disables the whole file.
 *
 * Debug output: append `?bs_track=debug` to the URL.
 */
(function () {
  "use strict";

  var cfg = window.bsGA4Config || {};
  var ID = String(cfg.measurementId || "").trim();

  // Das Debug-Flag überlebt die Navigation: der Konfigurator sitzt auf den
  // Produktseiten, und wer mit `?bs_track=debug` auf der Startseite startet,
  // verliert den Parameter beim ersten Klick — genau dann, wenn es interessant
  // wird. `?bs_track=off` schaltet wieder ab.
  function debugEnabled() {
    var on = /[?&]bs_track=debug/.test(window.location.search);
    try {
      if (on) window.sessionStorage.setItem("bs_track_debug", "1");
      else if (/[?&]bs_track=off/.test(window.location.search)) {
        window.sessionStorage.removeItem("bs_track_debug");
        return false;
      }
      return on || window.sessionStorage.getItem("bs_track_debug") === "1";
    } catch (e) {
      return on;
    }
  }

  var DEBUG = debugEnabled();

  function log() {
    if (!DEBUG) return;
    try {
      console.log.apply(console, ["[bs-ga4]"].concat([].slice.call(arguments)));
    } catch (e) {}
  }

  if (!ID) {
    log("keine Measurement-ID konfiguriert — bs-ga4.js bleibt still");
    return;
  }

  // Erste Zeile im Debug-Log: sonst ist nicht unterscheidbar, ob keine Events
  // ankommen oder die Datei gar nicht läuft.
  log("aktiv für", ID, "— wartet auf bs:event von bs-clarity.js");

  // ---------------------------------------------------------------------------
  // gtag bootstrap
  // ---------------------------------------------------------------------------

  var scriptLoaded = false;
  var configured = false;

  // True when some other integration (Google & YouTube channel, a custom pixel
  // injected into the theme) already runs gtag.js for this measurement id.
  // Configuring it a second time would double every page_view.
  function foreignGtagPresent() {
    try {
      return !!(window.google_tag_manager && window.google_tag_manager[ID]);
    } catch (e) {
      return false;
    }
  }

  function ensureStub() {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== "function") {
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
    }
  }

  // Consent Mode v2 defaults. Must be pushed before anything else, hence at
  // module scope rather than after the consent answer is known.
  function setConsentDefaults() {
    ensureStub();
    window.gtag("consent", "default", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
      wait_for_update: 500,
    });
  }

  // Queues `config` into the dataLayer immediately. Events fired before
  // gtag.js arrives are therefore not lost — the library replays the queue.
  function configure() {
    if (configured) return;
    configured = true;
    ensureStub();
    window.gtag("js", new Date());
    if (foreignGtagPresent()) {
      // Someone else owns the page_view stream; we only add our events.
      log("gtag already configured elsewhere, skipping our config");
      return;
    }
    window.gtag("config", ID, {
      anonymize_ip: true,
      // Shopify's Google-channel pixel owns the page_view stream.
      send_page_view: cfg.sendPageView === true,
    });
  }

  function loadScript() {
    if (scriptLoaded) return;
    scriptLoaded = true;
    if (document.getElementById("bs-ga4-loader") || foreignGtagPresent()) {
      log("gtag.js already on the page");
      return;
    }
    var s = document.createElement("script");
    s.id = "bs-ga4-loader";
    s.async = true;
    s.src = "https://www.googletagmanager.com/gtag/js?id=" + encodeURIComponent(ID);
    document.head.appendChild(s);
    log("gtag.js requested");
  }

  // gtag.js is heavy and pure overhead for a bouncing visitor, so it waits for
  // the browser to go idle. Same trade-off the Vue app made.
  function loadWhenIdle() {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(loadScript, { timeout: 8000 });
    } else {
      window.setTimeout(loadScript, 3000);
    }
  }

  function grant() {
    ensureStub();
    window.gtag("consent", "update", { analytics_storage: "granted" });
    loadWhenIdle();
  }

  setConsentDefaults();
  configure();

  // ---------------------------------------------------------------------------
  // Consent — Shopify customer privacy API
  // ---------------------------------------------------------------------------

  var consentSettled = false;

  function checkConsent(where) {
    if (consentSettled) return;
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== "function") return;
    try {
      if (privacy.analyticsProcessingAllowed()) {
        consentSettled = true;
        log("analytics allowed (" + where + ")");
        grant();
      } else {
        log("analytics not allowed (" + where + ")");
      }
    } catch (e) {
      log("consent api threw", e);
    }
  }

  // Returning visitors never see the banner, so no event fires for them — ask
  // the API for the decision they made earlier.
  document.addEventListener("visitorConsentCollected", function () {
    checkConsent("banner");
  });

  if (window.Shopify && typeof window.Shopify.loadFeatures === "function") {
    window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], function (error) {
      if (error) {
        log("could not load consent-tracking-api", error);
        return;
      }
      checkConsent("api");
    });
  } else {
    log("Shopify.loadFeatures missing — no consent signal, staying silent");
  }

  // ---------------------------------------------------------------------------
  // Canonical event → GA4
  // ---------------------------------------------------------------------------

  // Names deliberately reuse the old Vue app's wording where the meaning is
  // identical (`form_step`, `complete_form`, `preview_generated`, …) so the
  // existing funnel report keeps working across the platform switch.
  var EVENT_MAP = {
    open: "begin_form",
    step_enter: "form_step",
    step_back: "form_step_back",
    step_slow: "form_step_slow",
    exit: "form_abandon",
    exit_page: "form_abandon",
    complete: "complete_form",
    preview_start: "preview_start",
    preview_done: "preview_generated",
    preview_slow: "preview_slow",
    preview_error: "preview_error",
    preview_exit: "preview_abandon",
    field_touch: "field_touch",
    field_hesitate: "field_hesitate",
    field_skip: "field_skip",
    field_rework: "field_rework",
    chip_switch: "field_switch",
    info_open: "field_info_open",
    field_error: "field_error",
    field_error_repeat: "field_error_repeat",
    upload_start: "upload_start",
    upload_done: "upload_done",
    upload_error: "upload_error",
    upload_blocked: "upload_blocked",
    city_no_result: "city_no_result",
    city_search_error: "city_search_error",
    city_typed_not_selected: "city_typed_not_selected",
    reject_age_under4: "reject_age_under_4",
    cart_error: "cart_error",
    // Entwurf-Persistenz: wie oft ein gespeicherter Stand den Funnel rettet und
    // aus welchem Grund er verworfen wird (reason: cart | club | folgebuch | expired).
    draft_restored: "draft_restored",
    draft_cleared: "draft_cleared",
    // Bewertungs-Zwischenseite: `mode` trägt den Kanal (mail | lesezeichen).
    review_link: "review_link",
  };

  // Sticky context, so every friction event answers "where did this happen?"
  // even though only step transitions carry the step themselves.
  var ctx = { book_type: "", variant: "", step: 0, step_id: "", max_step: 0 };

  function splitStep(raw) {
    // `2_appearance` → { step: 2, step_id: "appearance" }
    var s = String(raw || "");
    var m = /^(\d+)_(.*)$/.exec(s);
    if (!m) return { step: 0, step_id: s };
    return { step: parseInt(m[1], 10), step_id: m[2] };
  }

  function clean(v) {
    // GA4 truncates parameter values at 100 chars; do it here so the dashboard
    // never shows a silently cut value.
    return String(v == null ? "" : v).slice(0, 100);
  }

  function send(eventName, params) {
    ensureStub();
    var payload = params || {};
    payload.send_to = ID;
    log(eventName, payload);
    window.gtag("event", eventName, payload);
  }

  function paramsFor(name, p) {
    var out = {};
    var stepSource = p.step ? splitStep(p.step) : { step: ctx.step, step_id: ctx.step_id };

    // `step` reist als String: GA4 registriert numerische Parameter als Metrik,
    // eine String-Dimension darauf bliebe leer — und im Funnel wird auf
    // Dimensionswerte gefiltert.
    if (stepSource.step) out.step = String(stepSource.step);
    if (stepSource.step_id) out.step_id = clean(stepSource.step_id);
    if (p.book_type || ctx.book_type) out.book_type = clean(p.book_type || ctx.book_type);
    // A/B-Test-Variante an jedem Event, damit Trichter und Reibung je Variante
    // auswertbar sind (Kontrolle: "klassisch").
    if (ctx.variant) out.variant = clean(ctx.variant);
    if (p.field) out.field = clean(p.field);
    if (p.reason) out.reason = clean(p.reason);
    if (p.mode) out.mode = clean(p.mode);
    if (name === "exit") out.exit_type = "close";
    if (name === "exit_page") out.exit_type = "page";
    if (typeof p.ms === "number") out.duration_ms = Math.round(p.ms);
    if (typeof p.query_len === "number") out.query_len = p.query_len;
    // Wie alt der wiederhergestellte Entwurf war — zeigt, ob die 7 Tage TTL passen.
    if (p.age_days) out.age_days = clean(p.age_days);
    if (ctx.max_step) out.max_step = String(ctx.max_step);
    return out;
  }

  document.addEventListener("bs:event", function (e) {
    var detail = (e && e.detail) || {};
    var name = detail.name;
    var p = detail.params || {};
    if (!name) return;

    if (p.book_type) ctx.book_type = p.book_type;

    if (name === "step_enter") {
      var st = splitStep(p.step);
      ctx.step = st.step;
      ctx.step_id = st.step_id;
      if (st.step > ctx.max_step) ctx.max_step = st.step;
    }

    var gaName = EVENT_MAP[name];
    if (!gaName) {
      log("unmapped canonical event", name, p);
      return;
    }

    // `complete` is deliberately not followed by our own add_to_cart: the
    // Shopify pixel fires that from the real cart write, with price and
    // variant attached. `complete_form` right before it is the configurator's
    // own conversion, and the gap between the two is a real signal — it means
    // the cart write failed.
    send(gaName, paramsFor(name, p));
  });

  // Clarity tags become user properties. Only the short, aggregatable ones:
  // GA4 caps user property values at 36 characters, which the friction/error
  // field *lists* would blow past — those stay queryable through the `field`
  // event parameter instead.
  var USER_PROPERTY_MAP = {
    bs_outcome: "configurator_outcome",
    bs_max_step: "configurator_max_step",
    bs_book_type: "configurator_book_type",
    bs_variant: "configurator_variant",
  };

  document.addEventListener("bs:tag", function (e) {
    var detail = (e && e.detail) || {};

    // Der Buchtyp kommt aus dem DOM und wird als Tag gemeldet, bevor der erste
    // Schritt betreten wird. Ohne das hier trügen nur die Events einen
    // `book_type`, die der Konfigurator selbst schickt (preview_*, complete) —
    // die Aufschlüsselung im Trichter bliebe löchrig.
    if (detail.key === "bs_book_type" && detail.value) ctx.book_type = String(detail.value);
    if (detail.key === "bs_variant" && detail.value) ctx.variant = String(detail.value);

    var key = USER_PROPERTY_MAP[detail.key];
    if (!key) return;
    var value = detail.value;
    if (Object.prototype.toString.call(value) === "[object Array]") return;
    var props = {};
    props[key] = String(value == null ? "" : value).slice(0, 36);
    ensureStub();
    log("user_property", key, props[key]);
    window.gtag("set", "user_properties", props);
  });

})();
